<script lang="ts">
  import { onMount } from 'svelte';
  import { 
    Receipt, CheckCircle2, CreditCard, Droplets, Calendar, 
    ShieldCheck, AlertCircle, Building2, User, ArrowRight, Lock
  } from 'lucide-svelte';

  interface Props {
    params: {
      token: string;
    };
  }

  let { params }: Props = $props();

  let loading = $state(true);
  let error = $state('');
  let invoice = $state<any>(null);

  // Payment form state
  let paymentMethod = $state<'card' | 'ach' | 'apple_pay'>('card');
  let cardNumber = $state('•••• •••• •••• 4242');
  let cardExpiry = $state('12/28');
  let cardCvc = $state('•••');
  let paying = $state(false);
  let paySuccess = $state(false);
  let receiptRef = $state('');

  async function loadInvoice() {
    loading = true;
    error = '';
    try {
      const res = await fetch(`/api/pay/${params.token}`);
      if (!res.ok) {
        throw new Error('Payment link is invalid or has expired.');
      }
      const data = await res.json();
      invoice = data.invoice;
      if (invoice?.payment_status === 'paid') {
        paySuccess = true;
        receiptRef = invoice.payment_reference || 'PAID';
      }
    } catch (err: any) {
      error = err.message || 'Unable to load invoice details.';
    } finally {
      loading = false;
    }
  }

  async function submitPayment() {
    paying = true;
    try {
      const res = await fetch(`/api/pay/${params.token}/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_method: paymentMethod,
          reference: `ONLINE-${paymentMethod.toUpperCase()}-${Math.floor(100000 + Math.random() * 900000)}`
        })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Payment failed to process');
      }
      invoice = data.invoice;
      receiptRef = data.invoice?.payment_reference || 'TX-SUCCESS';
      paySuccess = true;
    } catch (err: any) {
      alert(`Payment error: ${err.message}`);
    } finally {
      paying = false;
    }
  }

  onMount(() => {
    if (params.token) {
      loadInvoice();
    } else {
      error = 'No payment token provided.';
      loading = false;
    }
  });

  function formatCurrency(amount: number | null | undefined): string {
    if (amount == null || isNaN(amount)) return '$0.00';
    return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
</script>

<div class="min-h-screen bg-base-200 flex flex-col justify-center items-center p-4">
  <div class="w-full max-w-lg">
    <!-- Brand Header -->
    <div class="text-center mb-6">
      <div class="inline-flex items-center justify-center p-3 bg-primary/10 rounded-2xl mb-2 text-primary">
        <Droplets size={32} />
      </div>
      <h1 class="text-2xl font-black text-base-content tracking-tight">Saya Resident Utility Pay</h1>
      <p class="text-xs text-base-content/60 mt-0.5">Secure End-to-End Water & RUBS Payment Gateway</p>
    </div>

    {#if loading}
      <div class="bg-base-100 p-8 rounded-2xl border border-base-300 shadow-xl text-center">
        <span class="loading loading-spinner loading-lg text-primary"></span>
        <p class="text-xs text-base-content/60 mt-3 font-semibold">Retrieving your utility statement…</p>
      </div>
    {:else if error}
      <div class="bg-base-100 p-8 rounded-2xl border border-error/20 shadow-xl text-center">
        <div class="p-3 bg-error/10 text-error rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
          <AlertCircle size={24} />
        </div>
        <h3 class="text-base font-bold text-base-content">Invoice Not Found</h3>
        <p class="text-xs text-base-content/60 mt-1">{error}</p>
        <p class="text-[11px] text-base-content/40 mt-3">Please check your email link or contact your property management office.</p>
      </div>
    {:else if paySuccess}
      <!-- Paid Receipt View -->
      <div class="bg-base-100 p-8 rounded-3xl border border-success/30 shadow-2xl animate-fade-in">
        <div class="text-center mb-6">
          <div class="p-3 bg-success/20 text-success rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3 shadow-inner">
            <CheckCircle2 size={36} />
          </div>
          <span class="badge badge-success text-white font-bold px-3 py-1 text-xs uppercase tracking-wider mb-2">
            Payment Confirmed
          </span>
          <h2 class="text-2xl font-black text-base-content">Thank You!</h2>
          <p class="text-xs text-base-content/60 mt-1">Your water bill payment has been successfully recorded.</p>
        </div>

        <div class="bg-base-200/60 p-4 rounded-2xl border border-base-300 space-y-2.5 text-xs mb-6 font-medium">
          <div class="flex justify-between items-center">
            <span class="text-base-content/60">Invoice Number</span>
            <span class="font-mono font-bold text-base-content">{invoice?.invoice_number}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-base-content/60">Unit & Resident</span>
            <span class="font-bold text-base-content">Unit {invoice?.unit_label} ({invoice?.tenant_name || 'Resident'})</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-base-content/60">Amount Paid</span>
            <span class="font-black text-sm text-success">{formatCurrency(invoice?.total_cost_usd)}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-base-content/60">Payment Reference</span>
            <span class="font-mono text-base-content/80">{receiptRef}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-base-content/60">Date & Time</span>
            <span class="text-base-content/80">{new Date().toLocaleString()}</span>
          </div>
        </div>

        <div class="text-center text-[11px] text-base-content/50 flex items-center justify-center gap-1.5">
          <ShieldCheck size={14} class="text-success" />
          <span>A copy of your receipt has been logged with property billing.</span>
        </div>
      </div>
    {:else if invoice}
      <!-- Unpaid Invoice & Payment Form -->
      <div class="bg-base-100 rounded-3xl border border-base-300 shadow-2xl overflow-hidden animate-fade-in">
        <!-- Invoice Header Card -->
        <div class="bg-gradient-to-br from-primary/10 via-base-100 to-base-200/50 p-6 border-b border-base-200">
          <div class="flex items-start justify-between gap-3">
            <div>
              <span class="text-[11px] font-semibold text-primary uppercase tracking-wider block">Utility Statement</span>
              <h2 class="text-lg font-bold text-base-content mt-0.5">Unit {invoice.unit_label}</h2>
              <p class="text-xs text-base-content/60">{invoice.tenant_name || 'Resident Occupant'}</p>
            </div>
            <div class="text-right">
              <span class="text-xs text-base-content/60 block">Amount Due</span>
              <div class="text-2xl font-black text-primary">{formatCurrency(invoice.total_cost_usd)}</div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-base-300/60 text-xs">
            <div>
              <span class="text-base-content/50 block text-[10px] uppercase font-bold">Billing Period</span>
              <span class="font-semibold text-base-content">{invoice.billing_period}</span>
            </div>
            <div class="text-right">
              <span class="text-base-content/50 block text-[10px] uppercase font-bold">Payment Due Date</span>
              <span class="font-bold text-error">{invoice.due_date}</span>
            </div>
          </div>
        </div>

        <!-- Breakdown Details -->
        <div class="p-6 space-y-4">
          <div class="bg-base-200/50 p-3.5 rounded-xl border border-base-200 space-y-2 text-xs">
            <div class="flex justify-between">
              <span class="text-base-content/70">Submetered Consumption ({Math.round(invoice.submetered_gallons)} gal)</span>
              <span class="font-semibold text-base-content">{formatCurrency(invoice.submetered_cost_usd)}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-base-content/70">Common Area Share (RUBS)</span>
              <span class="font-semibold text-base-content">{formatCurrency(invoice.common_area_cost_usd)}</span>
            </div>
            <div class="divider my-1"></div>
            <div class="flex justify-between font-bold text-sm">
              <span class="text-base-content">Total Billed</span>
              <span class="text-primary">{formatCurrency(invoice.total_cost_usd)}</span>
            </div>
          </div>

          <!-- Payment Method Selector -->
          <div>
            <label class="text-xs font-bold text-base-content/70 block mb-2">Select Payment Method</label>
            <div class="grid grid-cols-3 gap-2">
              <button 
                type="button"
                onclick={() => paymentMethod = 'card'}
                class="p-2.5 rounded-xl border text-xs font-semibold flex flex-col items-center gap-1.5 transition-all
                  {paymentMethod === 'card' ? 'border-primary bg-primary/10 text-primary ring-1 ring-primary' : 'border-base-300 hover:bg-base-200'}"
              >
                <CreditCard size={18} />
                <span>Card</span>
              </button>

              <button 
                type="button"
                onclick={() => paymentMethod = 'ach'}
                class="p-2.5 rounded-xl border text-xs font-semibold flex flex-col items-center gap-1.5 transition-all
                  {paymentMethod === 'ach' ? 'border-primary bg-primary/10 text-primary ring-1 ring-primary' : 'border-base-300 hover:bg-base-200'}"
              >
                <Building2 size={18} />
                <span>Bank (ACH)</span>
              </button>

              <button 
                type="button"
                onclick={() => paymentMethod = 'apple_pay'}
                class="p-2.5 rounded-xl border text-xs font-semibold flex flex-col items-center gap-1.5 transition-all
                  {paymentMethod === 'apple_pay' ? 'border-primary bg-primary/10 text-primary ring-1 ring-primary' : 'border-base-300 hover:bg-base-200'}"
              >
                <ShieldCheck size={18} />
                <span>Apple Pay</span>
              </button>
            </div>
          </div>

          <!-- Mock Card Details -->
          {#if paymentMethod === 'card'}
            <div class="space-y-3 pt-2">
              <div>
                <label class="text-[11px] font-bold text-base-content/60 block mb-1">Card Number</label>
                <input 
                  type="text" 
                  bind:value={cardNumber} 
                  class="input input-sm input-bordered w-full font-mono bg-base-100" 
                />
              </div>
              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="text-[11px] font-bold text-base-content/60 block mb-1">Expires</label>
                  <input 
                    type="text" 
                    bind:value={cardExpiry} 
                    class="input input-sm input-bordered w-full font-mono bg-base-100" 
                  />
                </div>
                <div>
                  <label class="text-[11px] font-bold text-base-content/60 block mb-1">CVC</label>
                  <input 
                    type="text" 
                    bind:value={cardCvc} 
                    class="input input-sm input-bordered w-full font-mono bg-base-100" 
                  />
                </div>
              </div>
            </div>
          {/if}

          <!-- Pay Button -->
          <button 
            type="button"
            onclick={submitPayment}
            disabled={paying}
            class="btn btn-primary w-full gap-2 text-sm font-bold shadow-lg shadow-primary/20 mt-4"
          >
            {#if paying}
              <span class="loading loading-spinner loading-sm"></span>
              <span>Processing Payment…</span>
            {:else}
              <Lock size={16} />
              <span>Pay {formatCurrency(invoice.total_cost_usd)} Now</span>
            {/if}
          </button>

          <p class="text-[10px] text-center text-base-content/50 flex items-center justify-center gap-1 pt-1">
            <Lock size={10} />
            <span>256-bit TLS encrypted transaction processed via Saya Secure Gateway</span>
          </p>
        </div>
      </div>
    {/if}
  </div>
</div>
